/**
 * Day / month sales-overview receipt — 576-dot bitmap renderer.
 *
 * The layout follows the reports receipt design: report identity first,
 * whitespace-separated sales and cash sections, one final total, an estimated
 * deposit note on daily reports, then report-generation and store details
 * above the shared PointCraft footer.
 */
import type {
  DailySummaryType,
  BusinessDataV2Type,
  DailySummaryByPeriod,
} from '@am3nd-org/pointcraft-types';

import { fPhone } from 'src/utils/format-number';
import { fReportCurrencyCents } from 'src/utils/report-formatters';

import type { PaperWidth } from './types';
import { createReceiptCanvas, appendBrandingFooter } from './canvas-toolkit';
import {
  dailyPeriodLabel,
  generatedAtLabel,
  monthlyPeriodParts,
  getSummaryReceiptAmounts,
  getSummaryReceiptSalesRows,
} from './summary-receipt.helpers';

type ReportPeriod =
  | { type: 'day'; date: string }
  | {
      type: 'month';
      monthLabel: string;
      dateRange: { start: string; end: string };
    };

type SummaryCanvasParams = {
  summary: DailySummaryType | DailySummaryByPeriod;
  business: BusinessDataV2Type;
  reportPeriod: ReportPeriod;
  paperWidth?: PaperWidth;
  generatedAt?: Date;
};

const ESTIMATE_DISCLAIMER =
  'Estimate only. May change due to transaction cutoffs, payment holds, or refunds.';

function businessAddress(business: BusinessDataV2Type): string {
  const address = business?.businessInfo.address;
  const street = [address?.street1, address?.street2].filter(Boolean).join(' ');
  const locality = [address?.city, address?.state].filter(Boolean).join(', ');
  return [street, locality].filter(Boolean).join(', ');
}

export function summaryCanvas({
  summary,
  business,
  reportPeriod,
  paperWidth,
  generatedAt = new Date(),
}: SummaryCanvasParams): string {
  const timeZone = business?.configurations?.timeZone ?? 'America/Chicago';
  const values = getSummaryReceiptAmounts(summary);
  const c = createReceiptCanvas({ paperWidth });
  const { px } = c;

  const rows = (items: Array<{ label: string; value: string; weight?: number }>, size = 26) => {
    items.forEach((item, index) => {
      c.row(item.label, item.value, size, item.weight ?? 400);
      if (index < items.length - 1) c.space(px(4));
    });
  };

  // The shared canvas starts at 28 dots; the design starts at 32.
  c.space(px(4));
  c.lines('Sales Overview', 40, 700, 'left');
  if (reportPeriod.type === 'day') {
    c.lines(dailyPeriodLabel(reportPeriod.date, timeZone), 24, 400, 'left');
  } else {
    const period = monthlyPeriodParts(reportPeriod.monthLabel, reportPeriod.dateRange, timeZone);
    c.inlineText(
      [{ text: period.prefix }, { text: period.month, weight: 700 }, { text: period.suffix }],
      24
    );
  }

  c.space(px(34));
  c.lines('SALES SUMMARY', 28, 700, 'left');
  c.space(px(8));
  rows(getSummaryReceiptSalesRows(values));
  c.space(px(8));
  c.row('Transactions', values.transactionCount.toLocaleString('en-US'), 26, 400);

  c.space(px(34));
  c.lines('TOTAL CASH', 28, 700, 'left');
  c.space(px(8));
  rows([
    { label: 'Cash Sales', value: fReportCurrencyCents(values.cashSalesCents) },
    {
      label: 'Expected Cash',
      value: fReportCurrencyCents(values.expectedCashCents),
      weight: 700,
    },
  ]);

  if (reportPeriod.type === 'day') {
    c.space(px(34));
    c.rule(0, px(12));
    c.row('Expected Deposit', fReportCurrencyCents(values.expectedDepositCents), 30, 700);
    c.space(px(10));
    c.lines(ESTIMATE_DISCLAIMER, 22, 400, 'left');
  }

  c.space(px(44));
  c.lines(generatedAtLabel(generatedAt, timeZone), 24, 400, 'center');
  c.space(px(16));
  c.lines((business?.name || 'Business').toUpperCase(), 26, 600, 'center');

  const address = businessAddress(business);
  if (address) c.lines(address, 26, 400, 'center');
  if (business?.businessInfo.phone) {
    c.lines(fPhone(business.businessInfo.phone), 26, 400, 'center');
  }

  appendBrandingFooter(c);
  return c.finish();
}
